import { SyntheticEvent, useCallback, useMemo } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { TextFieldProps, fieldToTextField } from 'formik-mui';
import { IManager } from '@sr/dto';
import { useReadOnlyRemoteCollection } from 'utils/remote-collection.hook';
import { fetchManagersDropdown } from './api';
import { useLinkedManagers } from 'entities/company/linked-managers/model';
import { useStateSelector } from 'shared/store';
import { mergeAutocompleteInputSx } from 'shared/ui/autocomplete-input-sx';
import {
  filterHiddenManagers,
  isToggleHiddenOption,
  renderManagerOption,
  splitToggleOptions,
  useHiddenManagerToggleState,
  withHiddenToggle
} from 'shared/ui/fired-manager-toggle';

type UserPickerFieldProps = {
  scope?: 'events';
  excludedManagers?: number[];
  isLinkedToCompany?: boolean;
  isClientSupplier?: boolean;
  includeUser?: boolean;
  clearable?: boolean;
  withNonSelectOption?: boolean;
  nonSelectLabel?: string;
  size?: 'small' | 'medium' | undefined;
  onItemChange?: (item: IManager | null) => void;
} & TextFieldProps;

export const UserPickerField = (props: UserPickerFieldProps) => {
  const {
    form: { setFieldValue },
    field: { name, value },
    size = undefined
  } = props;

  const {
    scope,
    isLinkedToCompany = false,
    includeUser = false,
    clearable = false,
    withNonSelectOption = false,
    isClientSupplier,
    nonSelectLabel,
    excludedManagers,
    onItemChange,
    ...textFieldProps
  } = props;

  const currentUser = useStateSelector((store) => store.auth.user);

  const { items: allManagers, isLoading: fetching } = useManagersDropdownList(scope);
  const {
    links: { items: linkedManagers }
  } = useLinkedManagers();

  const allFilteredManagers = useMemo(() => {
    const filteredManagers = isLinkedToCompany
      ? linkedManagers.filter((m) => m.linkType === 'link').map(({ manager }) => manager)
      : allManagers;

    if (includeUser && currentUser) {
      const isUserInManagers = filteredManagers.some(
        (item: IManager) => item.id === currentUser.id
      );
      if (!isUserInManagers) {
        filteredManagers.push(currentUser);
      }

      if (isClientSupplier) {
        const subordinateManagers = allManagers.filter(
          (item: any) => item.supervisorId === currentUser.id
        );
        subordinateManagers.forEach((subordinateManager) => {
          const isAlreadyInList = filteredManagers.some(
            (manager) => manager.id === subordinateManager.id
          );
          if (!isAlreadyInList) {
            filteredManagers.push(subordinateManager);
          }
        });
      }
    }

    return filteredManagers;
  }, [
    isLinkedToCompany,
    linkedManagers,
    allManagers,
    includeUser,
    currentUser,
    isClientSupplier
  ]);

  const { showHidden, toggleShowHidden, hasHiddenManagers } =
    useHiddenManagerToggleState(allFilteredManagers);

  const managers = useMemo(() => {
    const visible = filterHiddenManagers(allFilteredManagers, showHidden);

    const base = withNonSelectOption
      ? [
          {
            firstName: `- ${nonSelectLabel || 'Не выбран'}`,
            secondName: '-',
            id: 0
          } as IManager,
          ...visible
        ]
      : visible;

    return withHiddenToggle(base, { hasHiddenManagers });
  }, [
    allFilteredManagers,
    showHidden,
    hasHiddenManagers,
    nonSelectLabel,
    withNonSelectOption
  ]);

  const selectedManager = useMemo(() => {
    return managers.find((manager: IManager) => manager.id === value) || null;
  }, [managers, value]);

  const onChange = useCallback(
    (_event: SyntheticEvent<Element, Event>, newValue: IManager | null) => {
      if (isToggleHiddenOption(newValue)) {
        toggleShowHidden();
        return;
      }
      if (!newValue && !clearable) return;
      setFieldValue(name, newValue ? newValue.id : null);
      onItemChange?.(newValue);
    },
    [clearable, setFieldValue, name, onItemChange, toggleShowHidden]
  );

  return (
    <Autocomplete
      fullWidth
      value={selectedManager}
      options={managers}
      disabled={props.disabled}
      disableClearable={!clearable}
      size={size}
      onChange={onChange}
      isOptionEqualToValue={(option, value) =>
        !value || option.id === value.id || (option.id === 0 && value.id === 0)
      }
      getOptionLabel={(option) => {
        if (isToggleHiddenOption(option)) return '';
        return option.id === 0
          ? option.firstName
          : `${option.firstName} ${option.secondName}`;
      }}
      getOptionDisabled={(o) =>
        excludedManagers ? excludedManagers.includes(o.id) : false
      }
      filterOptions={splitToggleOptions}
      loading={fetching}
      loadingText="Загрузка..."
      noOptionsText="Ничего не найдено"
      renderOption={(optionProps, option) =>
        renderManagerOption(optionProps, option, {
          showHidden,
          onToggle: toggleShowHidden,
          getLabel: (o) =>
            o.id === 0
              ? o.firstName ?? ''
              : `${o.firstName ?? ''} ${o.secondName ?? ''}`
        })
      }
      renderInput={(params) => {
        const { sx, ...restFieldProps } = fieldToTextField(textFieldProps);
        return (
          <TextField
            {...restFieldProps}
            name={name}
            {...params}
            disabled={props.disabled}
            label={textFieldProps.label ?? 'Менеджер'}
            placeholder="Начните набирать имя..."
            sx={mergeAutocompleteInputSx(sx)}
          />
        );
      }}
    />
  );
};

export const useManagersDropdownList = (scope?: 'events') => {
  return useReadOnlyRemoteCollection<IManager>({
    fetchHandler: useCallback(() => fetchManagersDropdown(scope), [scope])
  });
};